'use client';

import { c as _c } from "react/compiler-runtime";
import type { MergeabilityStatus } from '@/lib/hooks/mergeability-status/get-mergeability-status.server';
import type { PrV2CheckRunResponse, PrV2ReviewResponse } from '@/lib/pr-v2/types';
import type { Reviewer } from '@/types/github';
import { ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { PrV2MetadataSummary } from '../shared/pr-v2-metadata';
import { PrLabels } from '../header/pr-v2-labels';
import { PrV2FaviconState } from '../pr-v2-favicon-state';
import { PrV2Checks } from '../shared/pr-v2-checks';
import { SIDEBAR_HEADING_CLASS } from '../shared/sidebar-heading-classes';
import { buildPrV2Labels } from './pr-v2-sidebar-model';
import { RequestedTeamRow } from './requested-team-row';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { ReviewerState, ReviewType } from '@/types/github';
import { isBotLogin, isMrgeBot } from '@/utils/github/bot-detection';
type PrV2PublicSidebarProps = {
  headSha: string | null;
  metadata: PrV2MetadataSummary;
  reviews: PrV2ReviewResponse[];
  checks: PrV2CheckRunResponse[];
  initialMergeabilityStatus?: MergeabilityStatus | null;
  checksLoading?: boolean;
  checksError?: string | null;
};
type SidebarReviewer = {
  login: string;
  avatarUrl: string | null;
  state: Reviewer['state'] | string;
};
function initials(login: string | null): string {
  if (!login) {
    return 'NA';
  }
  return login.slice(0, 2).toUpperCase();
}
function toTimestamp(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}
function normalizeReviewState(state: string | null): Reviewer['state'] | string {
  const normalized = state?.toUpperCase();
  switch (normalized) {
    case ReviewerState.APPROVED:
      return ReviewerState.APPROVED;
    case ReviewType.REQUEST_CHANGES:
    case ReviewerState.CHANGES_REQUESTED:
      return ReviewerState.CHANGES_REQUESTED;
    case ReviewerState.DISMISSED:
      return ReviewerState.DISMISSED;
    case ReviewerState.COMMENTED:
      return ReviewerState.COMMENTED;
    default:
      return normalized || ReviewerState.REQUESTED_REVIEW;
  }
}
function mergeReviewerState(currentState: Reviewer['state'] | string, nextState: Reviewer['state'] | string): Reviewer['state'] | string {
  if (nextState === ReviewerState.COMMENTED && (currentState === ReviewerState.APPROVED || currentState === ReviewerState.CHANGES_REQUESTED || currentState === ReviewerState.DISMISSED)) {
    return currentState;
  }
  return nextState;
}
function buildReviewers(metadata: PrV2MetadataSummary, reviews: PrV2ReviewResponse[]): SidebarReviewer[] {
  const reviewerMap = new Map<string, SidebarReviewer>();
  metadata.reviewers.forEach(reviewer => {
    reviewerMap.set(reviewer.login, {
      login: reviewer.login,
      avatarUrl: reviewer.avatarUrl,
      state: ReviewerState.REQUESTED_REVIEW
    });
  });
  const sortedReviews = [...reviews].sort((a, b) => toTimestamp(a.submittedAt) - toTimestamp(b.submittedAt));
  sortedReviews.forEach(review => {
    if (!review.userLogin) {
      return;
    }
    const currentReviewer = reviewerMap.get(review.userLogin);
    const nextState = normalizeReviewState(review.state);
    reviewerMap.set(review.userLogin, {
      login: review.userLogin,
      avatarUrl: review.userAvatarUrl,
      state: currentReviewer ? mergeReviewerState(currentReviewer.state, nextState) : nextState
    });
  });
  return Array.from(reviewerMap.values()).filter(reviewer => !isBotLogin(reviewer.login) && !isMrgeBot(reviewer.login));
}
export function PrV2PublicSidebar(t0) {
  const $ = _c(32);
  const {
    headSha,
    metadata,
    reviews,
    checks,
    initialMergeabilityStatus: t1,
    checksLoading: t2,
    checksError: t3
  } = t0;
  const initialMergeabilityStatus = t1 === undefined ? null : t1;
  const checksLoading = t2 === undefined ? false : t2;
  const checksError = t3 === undefined ? null : t3;
  const isMobile = useIsMobile();
  const [isMobileLabelsExpanded, setIsMobileLabelsExpanded] = useState(false);
  let t4;
  if ($[0] !== metadata) {
    t4 = buildPrV2Labels(metadata);
    $[0] = metadata;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const labels = t4;
  let t5;
  if ($[2] !== metadata || $[3] !== reviews) {
    t5 = buildReviewers(metadata, reviews);
    $[2] = metadata;
    $[3] = reviews;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const reviewers = t5;
  const hasReviewerSignals = reviewers.length > 0 || metadata.requestedTeams.length > 0;
  const isLabelsExpanded = isMobile ? isMobileLabelsExpanded : true;
  let t6;
  if ($[5] !== checks || $[6] !== headSha || $[7] !== initialMergeabilityStatus) {
    t6 = <PrV2FaviconState checks={checks} currentHeadSha={headSha} mergeabilityStatus={initialMergeabilityStatus} hasActiveCubicReview={false} />;
    $[5] = checks;
    $[6] = headSha;
    $[7] = initialMergeabilityStatus;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <h3 className={`mb-3 ${SIDEBAR_HEADING_CLASS}`}>Reviewers</h3>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== hasReviewerSignals || $[11] !== reviewers) {
    t8 = !hasReviewerSignals ? <p className="text-sm text-zinc-500">No reviewers yet</p> : reviewers.map(_temp);
    $[10] = hasReviewerSignals;
    $[11] = reviewers;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== metadata.requestedTeams) {
    t9 = metadata.requestedTeams.map(_temp2);
    $[13] = metadata.requestedTeams;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9) {
    t10 = <section className="w-full md:w-[220px]">{t7}<div className="w-full space-y-2 pr-0.5">{t8}{t9}</div></section>;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== checks || $[19] !== checksError || $[20] !== checksLoading || $[21] !== headSha) {
    t11 = <PrV2Checks checks={checks} headSha={headSha} checksLoading={checksLoading} checksError={checksError} className="mt-6 w-full md:w-[220px]" />;
    $[18] = checks;
    $[19] = checksError;
    $[20] = checksLoading;
    $[21] = headSha;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== isLabelsExpanded || $[24] !== isMobile || $[25] !== labels) {
    t12 = labels.length > 0 ? <section className="mt-6 w-full md:w-[220px]">{isMobile ? <><Button type="button" variant="ghost" className="group/labels mb-2 flex h-auto w-full items-center justify-between gap-3 px-0 py-0" onClick={() => setIsMobileLabelsExpanded(_temp3)} aria-expanded={isLabelsExpanded} aria-label={isLabelsExpanded ? "Collapse labels" : "Expand labels"}><span className="flex items-center gap-1.5"><span className={SIDEBAR_HEADING_CLASS}>Labels</span><ChevronRight className={`size-3.5 text-zinc-500 opacity-0 transition-all group-hover/labels:opacity-100 ${isLabelsExpanded ? "rotate-90" : ""}`} /></span></Button>{isLabelsExpanded ? <PrLabels labels={labels} /> : null}</> : <div className="flex flex-col items-start justify-between"><h3 className={`mb-2 ${SIDEBAR_HEADING_CLASS}`}>Labels</h3><PrLabels labels={labels} /></div>}</section> : null;
    $[23] = isLabelsExpanded;
    $[24] = isMobile;
    $[25] = labels;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t12 || $[30] !== t6) {
    t13 = <aside className="pb-3 pl-0 md:pb-0">{t6}{t10}{t11}{t12}</aside>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t6;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
}
function _temp3(current) {
  return !current;
}
function _temp2(team) {
  return <RequestedTeamRow key={team.slug ?? team.name} team={team} />;
}
function _temp(reviewer) {
  return <div key={reviewer.login} className="flex items-center gap-2 text-zinc-300"><Avatar className="size-5"><AvatarImage src={reviewer.avatarUrl ?? ""} alt={`${reviewer.login}'s avatar`} /><AvatarFallback>{initials(reviewer.login)}</AvatarFallback></Avatar><span className="min-w-0 flex-1 truncate text-sm">{reviewer.login}</span><span className="shrink-0 text-[11px] text-zinc-500">{reviewer.state}</span></div>;
}